import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { SpinnerGap } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { NextPinHint } from '@/components/NextPinHint';

const MONTHS = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export default function CommissionsPage() {
  const navigate = useNavigate();
  const { activeId } = useAuth();
  const [resume, setResume] = useState(null);
  const [allComm, setAllComm] = useState([]);
  const [graph, setGraph] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('resumo');
  const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);
  const fmtN = (v) => new Intl.NumberFormat('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.post('/comissoes/resume').then(r => setResume(r.data)).catch(() => {}),
      api.post('/comissoes/all').then(r => setAllComm(r.data?.data || [])).catch(() => {}),
      api.post('/comissoes/last12months').then(r => setGraph(r.data?.data || [])).catch(() => {}),
      api.post('/comissoes/payments').then(r => setPayments(r.data?.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, [activeId]);

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0F1829]" /></div>;

  const chartData = graph.map(g => ({ name: `${MONTHS[g.Mes]} ${g.Ano}`, valor: g.ValorComissao || 0 }));

  return (
    <div data-testid="commissions-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>Comissões</h1>
        <p className="text-sm text-[#6B7280] mt-1">Resumo de comissões, volumes e pagamentos</p>
      </div>

      {resume && (
        <>
          {/* Row 1: Comissões */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <Card label="Ganhas" value={fmt(resume.Ganhas)} />
            <Card label="Pagas" value={fmt(resume.Pagas)} />
            <Card label="Disponível p/ Pagamento" value={fmt(resume.DisponivelPagamento)} color="#00C853" />
            <Card label="A Receber" value={fmt(resume.Receber)} color="#0F1829" />
          </div>

          {/* Row 2: NetPoints */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <Card label="NetPoints Pessoal" value={fmtN(resume.NetPointsPessoal)} />
            <Card label="NetPoints Cliente" value={fmtN(resume.NetPointsCliente)} />
            <Card label="NetPoints Rede" value={fmtN(resume.NetPointsRede)} />
            <Card label="Créditos" value={fmtN(resume.Creditos)} />
          </div>

          {/* Row 3: Volumes */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <Card label="Volume Mês" value={fmt(resume.VolumeMes)} />
            <Card label="Volume Trimestre" value={fmt(resume.VolumeTrimestre)} />
            <Card
              label="Volume Ano (Contrato)"
              value={fmt(resume.VolumeAno)}
              subtitle={resume.VolumeAnoCycleStart && resume.VolumeAnoCycleEnd
                ? `${fmtISO(resume.VolumeAnoCycleStart)} → ${fmtISO(resume.VolumeAnoCycleEnd)}`
                : null}
            />
            <Card label="Validade" value={resume.DataValidade || '-'} />
          </div>

          {resume.CurrentNpts && (
            <div className="mb-6">
              <NextPinHint
                npts={resume.CurrentNpts.netpoints_total}
                mesLabel={resume.CurrentNpts.mes ? `${MONTHS[resume.CurrentNpts.mes]} ${resume.CurrentNpts.ano}` : null}
              />
            </div>
          )}
        </>
      )}

      {/* Graph */}
      {chartData.length > 0 && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-6">
          <h3 className="text-sm font-semibold text-[#111827] mb-4">Comissões Últimos 12 Meses</h3>
          <div className="h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#6B7280' }} />
                <YAxis tick={{ fontSize: 10, fill: '#6B7280' }} />
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #E5E7EB' }} />
                <Bar dataKey="valor" fill="#0F1829" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-[#E5E7EB]">
        {[{key:'resumo',label:'Comissões Mensais'},{key:'payments',label:'Pagamentos'}].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${tab===t.key?'border-[#0F1829] text-[#0F1829]':'border-transparent text-[#6B7280] hover:text-[#111827]'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'resumo' && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                <TH>Mês/Ano</TH>
                <TH align="right">Enc. Pessoal</TH>
                <TH align="right">Enc. Clientes</TH>
                <TH align="right">Enc. Talentos</TH>
                <TH align="right">Vol. Encomendas</TH>
                <TH align="right">NP Total</TH>
                <TH align="right">Taxa Bonus</TH>
                <TH align="right">Bonus Group</TH>
                <TH align="right">Bonus Líder</TH>
                <TH align="right">Comissão</TH>
                <TH align="right">Total</TH>
              </tr></thead>
              <tbody>
                {allComm.map((c) => (
                  <tr key={`comm-${c.Ano}-${c.Mes}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer transition-colors" onClick={() => navigate(`/comissoes/${c.Ano}/${c.Mes}`)}>
                    <td className="px-3 py-2.5 text-sm font-medium text-[#111827] whitespace-nowrap">{MONTHS[c.Mes]} {c.Ano}</td>
                    <td className="px-3 py-2.5 text-sm font-mono text-right text-[#6B7280]">
                      {c.NrEncomendasPessoal > 0 ? <>{c.NrEncomendasPessoal} <span className="text-[10px]">({fmt(c.ValorEncomendasPessoal)})</span></> : '-'}
                    </td>
                    <td className="px-3 py-2.5 text-sm font-mono text-right text-[#6B7280]">
                      {c.NrEncomendasClientes > 0 ? <>{c.NrEncomendasClientes} <span className="text-[10px]">({fmt(c.ValorEncomendasClientes)})</span></> : '-'}
                    </td>
                    <td className="px-3 py-2.5 text-sm font-mono text-right text-[#6B7280]">
                      {c.NrEncomendasTalentos > 0 ? <>{c.NrEncomendasTalentos} <span className="text-[10px]">({fmt(c.ValorEncomendasTalentos)})</span></> : '-'}
                    </td>
                    <td className="px-3 py-2.5 text-sm font-mono text-right text-[#111827]">{fmt(c.ValorEncomendas)}</td>
                    <td className="px-3 py-2.5 text-sm font-mono text-right text-[#6B7280]">{fmtN(c.NetPointsTotal)}</td>
                    <td className="px-3 py-2.5 text-sm font-mono text-right text-[#6B7280]">{c.TaxaBonusGroup > 0 ? `${c.TaxaBonusGroup}%` : '-'}</td>
                    <td className="px-3 py-2.5 text-sm font-mono text-right text-[#6B7280]">{c.ValorBonusGroup > 0 ? fmt(c.ValorBonusGroup) : '-'}</td>
                    <td className="px-3 py-2.5 text-sm font-mono text-right text-[#6B7280]">{c.ValorBonusLider > 0 ? fmt(c.ValorBonusLider) : '-'}</td>
                    <td className="px-3 py-2.5 text-sm font-mono text-right text-[#0F1829] font-semibold">{fmt(c.ValorComissao)}</td>
                    <td className="px-3 py-2.5 text-sm font-mono text-right text-[#111827] font-bold">{fmt(c.ValorTotal)}</td>
                  </tr>
                ))}
              </tbody>
              {allComm.length > 0 && (
                <tfoot>
                  <tr className="bg-[#F9FAFB] border-t-2 border-[#E5E7EB] font-semibold">
                    <td className="px-3 py-3 text-sm text-[#111827]">Total</td>
                    <td className="px-3 py-3 text-sm font-mono text-right">{allComm.reduce((s,c) => s + (c.NrEncomendasPessoal||0), 0)}</td>
                    <td className="px-3 py-3 text-sm font-mono text-right">{allComm.reduce((s,c) => s + (c.NrEncomendasClientes||0), 0)}</td>
                    <td className="px-3 py-3 text-sm font-mono text-right">{allComm.reduce((s,c) => s + (c.NrEncomendasTalentos||0), 0)}</td>
                    <td className="px-3 py-3 text-sm font-mono text-right">{fmt(allComm.reduce((s,c) => s + (c.ValorEncomendas||0), 0))}</td>
                    <td className="px-3 py-3 text-sm font-mono text-right">{fmtN(allComm.reduce((s,c) => s + (c.NetPointsTotal||0), 0))}</td>
                    <td className="px-3 py-3"></td>
                    <td className="px-3 py-3 text-sm font-mono text-right">{fmt(allComm.reduce((s,c) => s + (c.ValorBonusGroup||0), 0))}</td>
                    <td className="px-3 py-3 text-sm font-mono text-right">{fmt(allComm.reduce((s,c) => s + (c.ValorBonusLider||0), 0))}</td>
                    <td className="px-3 py-3 text-sm font-mono text-right text-[#0F1829]">{fmt(allComm.reduce((s,c) => s + (c.ValorComissao||0), 0))}</td>
                    <td className="px-3 py-3 text-sm font-mono text-right font-bold">{fmt(allComm.reduce((s,c) => s + (c.ValorTotal||0), 0))}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {allComm.length === 0 && <div className="text-center py-8 text-[#6B7280]">Sem comissões registadas</div>}
        </div>
      )}

      {tab === 'payments' && (
        <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <TH>Data</TH>
              <TH>Tipo</TH>
              <TH align="right">Valor</TH>
              <TH>Observações</TH>
            </tr></thead>
            <tbody>
              {payments.map((p, i) => (
                <tr key={p.ID_ComissaoPagamento || `pay-${p.Data || ''}-${i}`} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                  <td className="px-3 py-2.5 text-sm text-[#111827]">{p.Data ? new Date(p.Data).toLocaleDateString('pt-PT') : '-'}</td>
                  <td className="px-3 py-2.5 text-sm text-[#6B7280]">{p.TipoPagamento}</td>
                  <td className="px-3 py-2.5 text-sm font-mono text-right text-[#111827]">{fmt(p.ValorPago)}</td>
                  <td className="px-3 py-2.5 text-sm text-[#6B7280] truncate max-w-[200px]">{p.Observacoes || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          {payments.length === 0 && <div className="text-center py-8 text-[#6B7280]">Sem pagamentos</div>}
        </div>
      )}
    </div>
  );
}

function Card({ label, value, color, subtitle }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280]">{label}</p>
      <p className="text-lg font-bold font-mono mt-0.5" style={{ color: color || '#111827', fontFamily: "'JetBrains Mono', monospace" }}>{value}</p>
      {subtitle && <p className="text-[10px] text-[#6B7280] mt-0.5 font-mono">{subtitle}</p>}
    </div>
  );
}

function fmtISO(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function TH({ children, align = 'left' }) {
  return <th className={`text-${align} text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] px-3 py-2.5 whitespace-nowrap`}>{children}</th>;
}
